/**
 * Shared Logo Component
 *
 * Single source of truth for the storefront logo (`/public/logo.webp`, generated from
 * `/public/logo.png` by `scripts/generate-brand-image-assets.mjs`).
 */

import Image from "next/image";
import { brandConfig } from "@/config/brand";
import { cn } from "@/lib/utils";

interface LogoProps {
	className?: string;
	/** Accessible label for the logo */
	ariaLabel?: string;
	/** Nur wo das Logo das LCP-Element ist (Landingpage, Checkout-Weiterleitung): Preload im Head. */
	priority?: boolean;
	/**
	 * Im ersten Viewport sichtbar, aber nicht LCP (Header): sofort laden, ohne Preload.
	 * `fetchPriority="low"` ist nötig, weil Reacts Server-Renderer sonst für jedes nicht-lazy `<img>`
	 * selbst einen Preload erzeugt.
	 */
	eager?: boolean;
	/** `sizes` attribute for next/image (wirksam nur mit aktivem Optimierer). */
	sizes?: string;
}

/**
 * Bergdorf Bioscience logo
 *
 * Uses explicit width/height for `next/image` while flexible sizing
 * is applied via `className` (e.g. `h-5 w-auto sm:h-7`).
 */
export const Logo = ({
	className,
	ariaLabel,
	priority = false,
	eager = false,
	sizes = "(min-width: 640px) 228px, 180px",
}: LogoProps) => {
	const alt = ariaLabel ?? brandConfig.logoAriaLabel;
	const loadingProps = priority
		? { preload: true }
		: eager
			? { loading: "eager" as const, fetchPriority: "low" as const }
			: { loading: "lazy" as const };

	return (
		<Image
			src="/logo.webp"
			alt={alt}
			width={400}
			height={126}
			{...loadingProps}
			sizes={sizes}
			className={cn("h-auto w-auto object-contain object-left", className)}
		/>
	);
};
